import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { setExchangeRateSchema, type SetExchangeRateInput } from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { DetailRow } from '@/components/DetailRow';
import { QueryState } from '@/components/QueryState';
import { TextField, TextareaField, FormActions, FormError } from '@/components/form';
import { useExchangeRate, useExchangeRateHistory, useSetExchangeRate } from '@/data/hooks';
import { parseDecimal } from '@/lib/decimal';

/**
 * Configuración — manages the live USD->VES exchange rate (admin/finance; the route is
 * already role-guarded, and the backend re-checks). The active rate drives VES display and
 * is locked onto new orders/payments at creation; editing it never recomputes past
 * documents. Every change is recorded in an auditable history below.
 */
export function ConfiguracionPage() {
  const rateQuery = useExchangeRate();
  const historyQuery = useExchangeRateHistory();
  const setRate = useSetExchangeRate();
  const current = rateQuery.data ?? null;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SetExchangeRateInput>({
    resolver: zodResolver(setExchangeRateSchema),
  });

  const onSubmit = async (values: SetExchangeRateInput) => {
    await setRate.mutateAsync(values);
    reset({ rate: '' as unknown as number, note: '' });
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Configuración"
        description="Tasa de cambio y parámetros de la plataforma."
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Active rate: the rate itself is the content, so it is shown large instead of as rows. */}
        <Card className="flex flex-col">
          <CardHeader>
            <CardTitle>Tasa de cambio activa</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-1 flex-col justify-center pt-0">
            <QueryState
              isLoading={rateQuery.isLoading}
              isError={rateQuery.isError}
              error={rateQuery.error}
              onRetry={() => void rateQuery.refetch()}
              isEmpty={!current}
              emptyTitle="Sin tasa configurada"
              emptyDescription="Aún no se ha establecido una tasa USD a VES. Defínela en el panel de la derecha."
            >
              {current ? (
                <div className="flex flex-col gap-5">
                  <div className="rounded-lg border border-border bg-surface-muted px-4 py-6 text-center">
                    <p className="text-3xl font-semibold tabular-nums tracking-tight text-text-primary sm:text-4xl">
                      Bs. {formatRate(current.rate)}
                    </p>
                    <p className="mt-1 text-sm text-text-muted">por cada US$ 1</p>
                  </div>
                  <dl>
                    <DetailRow label="Vigente desde">{formatDateTime(current.effectiveAt)}</DetailRow>
                    <DetailRow label="Nota">{current.note ?? '—'}</DetailRow>
                  </dl>
                </div>
              ) : null}
            </QueryState>
          </CardContent>
        </Card>

        {/* Set a new rate */}
        <Card>
          <CardHeader>
            <CardTitle>Actualizar tasa</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <form
              className="flex flex-col gap-4"
              onSubmit={handleSubmit(onSubmit)}
              // Typing again clears the "Tasa actualizada" notice of the previous save.
              onChange={() => {
                if (setRate.isSuccess || setRate.isError) setRate.reset();
              }}
              noValidate
            >
              {setRate.isError ? <FormError error={setRate.error} /> : null}
              {setRate.isSuccess && !setRate.isPending ? (
                <p
                  className="rounded-md border border-success bg-success-soft px-3 py-2 text-sm text-success"
                  role="status"
                >
                  Tasa actualizada. Se aplicará a nuevos pedidos y pagos.
                </p>
              ) : null}

              <TextField
                label="VES por 1 USD"
                inputMode="decimal"
                placeholder="Ej. 38,50"
                required
                error={errors.rate?.message}
                hint="Bolívares por un dólar. Solo afecta documentos futuros."
                {...register('rate', { setValueAs: parseDecimal })}
              />

              <TextareaField
                label="Nota (opcional)"
                placeholder="Ej. Tasa BCV del día"
                error={errors.note?.message}
                {...register('note')}
              />

              <FormActions>
                <Button type="submit" disabled={isSubmitting || setRate.isPending}>
                  {setRate.isPending ? 'Guardando…' : 'Guardar tasa'}
                </Button>
              </FormActions>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* History */}
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>Historial de tasas</CardTitle>
        </CardHeader>
        <QueryState
          isLoading={historyQuery.isLoading}
          isError={historyQuery.isError}
          error={historyQuery.error}
          onRetry={() => void historyQuery.refetch()}
          isEmpty={(historyQuery.data?.items.length ?? 0) === 0}
          loadingVariant="table"
          skeletonCols={3}
          emptyTitle="Sin cambios registrados"
          emptyDescription="Los cambios de tasa aparecerán aquí."
        >
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-text-muted">
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">VES por USD</th>
                <th className="px-4 py-3 font-medium">Nota</th>
                <th className="px-4 py-3 font-medium">Fijada por</th>
              </tr>
            </thead>
            <tbody>
              {(historyQuery.data?.items ?? []).map((entry) => (
                <tr key={entry.historyId} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 text-text-muted">{formatDateTime(entry.effectiveAt)}</td>
                  <td className="px-4 py-3 font-medium tabular-nums text-text-primary">
                    Bs. {formatRate(entry.rate)}
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-text-muted">{entry.note?.trim() || '—'}</td>
                  <td className="px-4 py-3 text-text-muted">{entry.setByName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </QueryState>
      </Card>
    </div>
  );
}

function formatRate(rate: number): string {
  return new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(rate);
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('es-VE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
